import { useEffect, useRef } from "react";
import {
  setCanvasAwarenessField,
  type CanvasAwarenessHandle,
} from "./awareness.js";
import { useCanvasViewport } from "./hooks/useCanvasViewport.js";

export function CanvasCursorPublisher({
  root,
  awareness,
}: {
  readonly root: HTMLDivElement | null;
  readonly awareness?: CanvasAwarenessHandle | null | undefined;
}): null {
  const viewport = useCanvasViewport();
  useCanvasCursorPublisher(root, awareness, viewport);
  return null;
}

function useCanvasCursorPublisher(
  root: HTMLDivElement | null,
  awareness: CanvasAwarenessHandle | null | undefined,
  viewport: { readonly x: number; readonly y: number; readonly zoom: number },
): void {
  const viewportRef = useRef(viewport);

  useEffect(() => {
    viewportRef.current = viewport;
  }, [viewport]);

  useEffect(() => {
    if (!root || !awareness) return undefined;

    let frame: number | null = null;
    let idleTimer: number | null = null;
    let pending: { x: number; y: number } | null = null;

    const clearIdleTimer = (): void => {
      if (idleTimer !== null) {
        window.clearTimeout(idleTimer);
        idleTimer = null;
      }
    };

    // The cursor is a live-pointer indicator: clear it when the pointer goes
    // idle, leaves the canvas, or the window blurs — there is no meaningful
    // pointer position to broadcast. Selection / active-widget are deliberately
    // NOT cleared here: they are durable "I'm on this widget" intent, so a peer
    // working in another window / on a second monitor must not vanish from
    // everyone's awareness on blur. That state clears on explicit deselect
    // (Canvas `onPaneClick` → `selectWidget(null)`), on editor close, on unmount
    // below, and — for a peer who actually leaves — via Yjs awareness dropping a
    // disconnected client.
    const clearCursor = (): void => {
      pending = null;
      clearIdleTimer();
      setCanvasAwarenessField(awareness, "cursor", null);
    };

    const flush = (): void => {
      frame = null;
      if (!pending) return;
      setCanvasAwarenessField(awareness, "cursor", pending);
    };

    const scheduleFlush = (): void => {
      if (frame !== null) return;
      frame = window.requestAnimationFrame(flush);
    };

    const onPointerMove = (event: PointerEvent): void => {
      const rect = root.getBoundingClientRect();
      const current = viewportRef.current;
      pending = {
        x: roundCanvasCoordinate(
          (event.clientX - rect.left - current.x) / current.zoom,
        ),
        y: roundCanvasCoordinate(
          (event.clientY - rect.top - current.y) / current.zoom,
        ),
      };
      scheduleFlush();
      clearIdleTimer();
      idleTimer = window.setTimeout(clearCursor, 2500);
    };

    root.addEventListener("pointermove", onPointerMove);
    root.addEventListener("pointerleave", clearCursor);
    window.addEventListener("blur", clearCursor);

    return () => {
      root.removeEventListener("pointermove", onPointerMove);
      root.removeEventListener("pointerleave", clearCursor);
      window.removeEventListener("blur", clearCursor);
      if (frame !== null) window.cancelAnimationFrame(frame);
      // This cleanup also runs when a host privacy-gates cursors at runtime.
      // Clear only cursor state here; Canvas owns semantic selection/activity
      // cleanup for a real awareness change or Canvas unmount.
      clearCursor();
    };
  }, [awareness, root]);
}

function roundCanvasCoordinate(value: number): number {
  return Math.round(value * 100) / 100;
}
